import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCopy, FaPen, FaPlus, FaTrash, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userAppData } from '../MainApp';
import Header from '../common/header.js';
import {
  getPerformanceManagementAdminData,
  savePerformanceManagementConfiguration,
  updatePerformanceManagerAssignment,
  updatePerformanceTemplateAssignment,
  getPerformanceManagementProgress,
  deletePerformanceManagementRecord,
} from './sysManagerPerformanceApi';
import './SysManagerPerformanceManagement.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const newId = () => (window.crypto?.randomUUID?.() || `pm-${Date.now()}-${Math.random().toString(16).slice(2)}`);

function emptyTemplate() {
  return {
    name: 'New template', minObjectives: 1, maxObjectives: 3, status: 'DRAFT',
    requiresManagerApproval: true,
    sections: { objectiveSetting: [], monitoring: [], finalReview: [], cpd: [] },
  };
}

/** System Manager editor for Performance Management configuration and reporting lines. */
export default function SysManagerPerformanceManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [profiles, setProfiles] = useState({});
  const [templates, setTemplates] = useState({});
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState('profiles');
  const [templateModal, setTemplateModal] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [templatePeopleModal, setTemplatePeopleModal] = useState(null);
  const [updatingTemplateStaff, setUpdatingTemplateStaff] = useState({});
  const [progress, setProgress] = useState({ profileId: '', records: [], counts: {} });
  const [deleteRecordModal, setDeleteRecordModal] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState('');
  const configurationLoadedRef = useRef(false);
  const savedConfigurationRef = useRef('');
  const saveInFlightRef = useRef(false);
  const queuedSaveRef = useRef(null);
  const revisionRef = useRef(0);

  const sortedStaff = useMemo(
    () => [...staff].sort((a, b) => String(a.displayName).localeCompare(String(b.displayName))),
    [staff],
  );

  useEffect(() => {
    const perms = userAppData?.sysManager || {};
    if (Number(perms.perms ?? 0) !== 1 || perms.performanceManagementAdmin !== true) {
      navigate('/app');
      return;
    }
    loadData();
  }, [navigate]);

  useEffect(() => {
    if (!configurationLoadedRef.current || loading) return undefined;
    const payload = { profiles, templates };
    const signature = JSON.stringify(payload);
    if (signature === savedConfigurationRef.current) {
      // If an older snapshot is currently saving, queue this restored state so
      // the older request cannot become the final persisted configuration.
      if (saveInFlightRef.current) queuedSaveRef.current = { payload, signature };
      return undefined;
    }

    setSaveStatus('pending');
    const timer = window.setTimeout(() => autoSaveConfiguration(payload, signature), 900);
    return () => window.clearTimeout(timer);
    // autoSaveConfiguration deliberately accepts the captured state snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, templates, loading]);

  async function loadData() {
    setLoading(true);
    try {
      const response = await getPerformanceManagementAdminData();
      const loadedProfiles = response.profiles || {};
      const loadedTemplates = response.templates || {};
      revisionRef.current = Number(response.revision || 0);
      savedConfigurationRef.current = JSON.stringify({ profiles: loadedProfiles, templates: loadedTemplates });
      configurationLoadedRef.current = true;
      setProfiles(loadedProfiles);
      setTemplates(loadedTemplates);
      setStaff(Array.isArray(response.staff) ? response.staff : []);
      setSaveStatus('saved');
    } catch (error) {
      console.error(error);
      toast.error('❌ Failed to load Performance Management settings.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  function updateProfile(profileId, changes) {
    setProfiles((current) => ({ ...current, [profileId]: { ...current[profileId], ...changes } }));
  }

  function updateMonitoringWindow(profileId, windowIndex, changes) {
    const windows = [...(profiles[profileId]?.monitoringWindows || [])];
    windows[windowIndex] = { ...windows[windowIndex], ...changes };
    updateProfile(profileId, { monitoringWindows: windows });
  }

  function removeMonitoringWindow(profileId, windowIndex) {
    updateProfile(profileId, { monitoringWindows: (profiles[profileId]?.monitoringWindows || []).filter((_, index) => index !== windowIndex) });
  }

  function updateTemplate(templateId, changes) {
    setTemplates((current) => ({ ...current, [templateId]: { ...current[templateId], ...changes } }));
  }

  /** Persist one stable snapshot while serialising overlapping auto-save calls. */
  async function autoSaveConfiguration(payload, signature) {
    if (saveInFlightRef.current) {
      queuedSaveRef.current = { payload, signature };
      return;
    }
    saveInFlightRef.current = true;
    setSaveStatus('saving');
    try {
      const response = await savePerformanceManagementConfiguration({ ...payload, revision: revisionRef.current });
      revisionRef.current = Number(response.revision || revisionRef.current);
      savedConfigurationRef.current = signature;
      setSaveStatus('saved');
    } catch (error) {
      console.error(error);
      setSaveStatus('error');
      toast.error('❌ Failed to save Performance Management configuration.', TOAST_CONFIG);
    } finally {
      saveInFlightRef.current = false;
      const queuedSave = queuedSaveRef.current;
      queuedSaveRef.current = null;
      if (queuedSave && queuedSave.signature !== savedConfigurationRef.current) {
        autoSaveConfiguration(queuedSave.payload, queuedSave.signature);
      }
    }
  }

  /** Retry the latest editor state after a failed automatic save. */
  function retryAutoSave() {
    const payload = { profiles, templates };
    autoSaveConfiguration(payload, JSON.stringify(payload));
  }

  async function changeManager(staffMember, managerCogId) {
    try {
      const response = await updatePerformanceManagerAssignment({
        targetCogId: staffMember.cogId,
        managerCogId,
      });
      setStaff((current) => current.map((item) => (
        item.cogId === staffMember.cogId ? { ...item, managerCogId: response.managerCogId } : item
      )));
      toast.success(`✅ Updated manager for ${staffMember.displayName}.`, TOAST_CONFIG);
    } catch (error) {
      console.error(error);
      toast.error(`❌ Failed to update manager for ${staffMember.displayName}.`, TOAST_CONFIG);
    }
  }

  function openTemplatePeopleModal(templateId, template) {
    setUpdatingTemplateStaff({});
    setTemplatePeopleModal({ templateId, templateName: template.name || 'Unnamed template' });
  }

  async function changeTemplateAccess(staffMember, enabled) {
    const templateId = templatePeopleModal?.templateId;
    if (!templateId || updatingTemplateStaff[staffMember.cogId]) return;

    setUpdatingTemplateStaff((current) => ({ ...current, [staffMember.cogId]: true }));
    try {
      const response = await updatePerformanceTemplateAssignment({
        targetCogId: staffMember.cogId,
        templateId,
        enabled,
      });
      const savedIds = Array.isArray(response.templateIds) ? response.templateIds : [];
      setStaff((current) => current.map((person) => (
        person.cogId === staffMember.cogId ? { ...person, templateIds: savedIds } : person
      )));
      toast.success(`✅ ${enabled ? 'Enabled' : 'Disabled'} ${templatePeopleModal.templateName} for ${staffMember.displayName}.`, TOAST_CONFIG);
    } catch (error) {
      console.error(error);
      toast.error(`❌ Failed to update template access for ${staffMember.displayName}.`, TOAST_CONFIG);
    } finally {
      setUpdatingTemplateStaff((current) => {
        const next = { ...current };
        delete next[staffMember.cogId];
        return next;
      });
    }
  }

  function openTemplateModal(templateId, template) {
    const copy = JSON.parse(JSON.stringify(template));
    if (!copy.sections) copy.sections = { objectiveSetting: copy.fields || [], monitoring: [], finalReview: [], cpd: [] };
    setTemplateModal({ templateId, template: copy });
  }

  async function loadProgress(profileId) {
    if (!profileId) { setProgress({ profileId: '', records: [], counts: {} }); return; }
    try {
      const response = await getPerformanceManagementProgress(profileId);
      setProgress(response);
    } catch (error) {
      console.error(error);
      toast.error('❌ Failed to load Performance Management progress.', TOAST_CONFIG);
    }
  }

  /** Permanently delete an administrator-selected record and refresh the current report. */
  async function confirmDeleteRecord() {
    const recordId = deleteRecordModal?.recordId;
    const profileId = progress.profileId;
    if (!recordId || deletingRecordId) return;
    setDeletingRecordId(recordId);
    try {
      await deletePerformanceManagementRecord(recordId);
      setDeleteRecordModal(null);
      toast.success('✅ Performance Management record deleted.', TOAST_CONFIG);
      await loadProgress(profileId);
    } catch (error) {
      console.error(error);
      toast.error('❌ Failed to delete the Performance Management record.', TOAST_CONFIG);
    } finally {
      setDeletingRecordId('');
    }
  }

  /** Ask for a unique name before preparing an independent template copy. */
  function openDuplicateModal(templateId, template) {
    setDuplicateModal({
      sourceTemplateId: templateId,
      sourceTemplate: JSON.parse(JSON.stringify(template)),
      name: `${String(template.name || 'Unnamed template').trim()} copy`,
      error: '',
    });
  }

  /** Duplicate a template locally, regenerating every identity-bearing field. */
  function duplicateTemplate(event) {
    event.preventDefault();
    const name = String(duplicateModal?.name || '').trim();
    if (!name) {
      setDuplicateModal((current) => ({ ...current, error: 'Enter a name for the new template.' }));
      return;
    }
    if (name.length > 120) {
      setDuplicateModal((current) => ({ ...current, error: 'Template names must be 120 characters or fewer.' }));
      return;
    }
    if (Object.values(templates).some((template) => String(template?.name || '').trim().toLowerCase() === name.toLowerCase())) {
      setDuplicateModal((current) => ({ ...current, error: 'A template with this name already exists.' }));
      return;
    }

    const templateId = newId();
    const source = duplicateModal.sourceTemplate;
    const template = {
      ...source,
      name,
      status: 'DRAFT',
      sections: Object.fromEntries(Object.entries(source.sections || {}).map(([sectionName, fields]) => [
        sectionName,
        (fields || []).map((field) => ({ ...field, id: newId(), values: (field.values || []).map((option) => ({ ...option, id: newId() })) })),
      ])),
    };
    setTemplates((current) => ({ ...current, [templateId]: template }));
    setDuplicateModal(null);
    openTemplateModal(templateId, template);
    toast.success('✅ Template duplicated. Review it, then save your changes.', TOAST_CONFIG);
  }

  function updateTemplateDraft(changes) {
    setTemplateModal((current) => ({ ...current, template: { ...current.template, ...changes } }));
  }

  function applyTemplateDraft() {
    updateTemplate(templateModal.templateId, templateModal.template);
    setTemplateModal(null);
  }

  function updateSection(sectionName, fields) {
    updateTemplateDraft({ sections: { ...(templateModal.template.sections || {}), [sectionName]: fields } });
  }

  function addField(sectionName) {
    const fields = [...(templateModal.template.sections?.[sectionName] || []), { id: newId(), label: 'New field', type: 'TEXT', owner: sectionName === 'objectiveSetting' ? 'STAFF' : 'BOTH', required: false, values: [] }];
    updateSection(sectionName, fields);
  }

  function updateField(sectionName, fieldIndex, changes) {
    const fields = [...(templateModal.template.sections?.[sectionName] || [])];
    fields[fieldIndex] = { ...fields[fieldIndex], ...changes };
    updateSection(sectionName, fields);
  }

  function removeField(sectionName, fieldIndex) {
    updateSection(sectionName, (templateModal.template.sections?.[sectionName] || []).filter((_, index) => index !== fieldIndex));
  }

  function addDropdownOption(sectionName, fieldIndex) {
    const fields = [...(templateModal.template.sections?.[sectionName] || [])];
    const field = fields[fieldIndex] || {};
    fields[fieldIndex] = {
      ...field,
      values: [...(field.values || []), { id: newId(), text: '' }],
    };
    updateSection(sectionName, fields);
  }

  function updateDropdownOption(sectionName, fieldIndex, optionIndex, text) {
    const fields = [...(templateModal.template.sections?.[sectionName] || [])];
    const field = fields[fieldIndex] || {};
    const values = [...(field.values || [])];
    values[optionIndex] = { ...values[optionIndex], text };
    fields[fieldIndex] = { ...field, values };
    updateSection(sectionName, fields);
  }

  function removeDropdownOption(sectionName, fieldIndex, optionIndex) {
    const fields = [...(templateModal.template.sections?.[sectionName] || [])];
    const field = fields[fieldIndex] || {};
    fields[fieldIndex] = {
      ...field,
      values: (field.values || []).filter((_, index) => index !== optionIndex),
    };
    updateSection(sectionName, fields);
  }

  if (loading) return <div className='main_div'><div className='pmAdmin_status'>Loading Performance Management settings…</div></div>;

  return (
    <div className='main_div'>
      <div className='internal_main_div pmAdmin_container'>
        <Header
          title='Performance Management'
          subtitle='Manage academic years, review templates, and maintain staff reporting assignments.'
          rightContent={(
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}>
              <FaArrowLeft aria-hidden='true' />
              Back to System Manager
            </button>
          )}
        />
        <nav className='pmAdmin_tabs' aria-label='Performance Management administration areas'>
          {['profiles', 'templates', 'assignments', 'progress'].map((name) => (
            <button type='button' className={tab === name ? 'active' : ''} onClick={() => setTab(name)} key={name}>
              {name === 'profiles' ? 'Academic years' : name === 'assignments' ? 'Managers' : name === 'progress' ? 'Progress' : 'Templates'}
            </button>
          ))}
        </nav>

        {tab === 'profiles' && <section className='pmAdmin_grid'>
          {Object.entries(profiles).map(([id, profile]) => <article className='pmAdmin_card' key={id}>
            <label>Name<input value={profile.profileName || ''} onChange={(e) => updateProfile(id, { profileName: e.target.value })} /></label>
            <label className='pmAdmin_check'><input type='checkbox' checked={profile.available === true} onChange={(e) => updateProfile(id, { available: e.target.checked })} /> Available for new records</label>
            <div className='pmAdmin_row'>
              <label>Objective setting opens<input type='date' value={profile.objectiveSetting?.opensAt || ''} onChange={(e) => updateProfile(id, { objectiveSetting: { ...(profile.objectiveSetting || {}), opensAt: e.target.value } })} /></label>
              <label>Objectives due<input type='date' value={profile.objectiveSetting?.dueAt || ''} onChange={(e) => updateProfile(id, { objectiveSetting: { ...(profile.objectiveSetting || {}), dueAt: e.target.value } })} /></label>
            </div>
            {(profile.monitoringWindows || []).map((window, windowIndex) => <div className='pmAdmin_row' key={window.reviewId}>
              <label>Monitoring name<input value={window.name || ''} onChange={(e) => updateMonitoringWindow(id, windowIndex, { name: e.target.value })} /></label>
              <label>Opens<input type='date' value={window.opensAt || ''} onChange={(e) => updateMonitoringWindow(id, windowIndex, { opensAt: e.target.value })} /></label>
              <label>Due<input type='date' value={window.dueAt || ''} onChange={(e) => updateMonitoringWindow(id, windowIndex, { dueAt: e.target.value })} /></label>
              <button type='button' className='pmAdmin_removeField' onClick={() => removeMonitoringWindow(id, windowIndex)} aria-label={`Remove ${window.name || 'monitoring window'}`}><FaTrash /></button>
            </div>)}
            <button type='button' className='pmAdmin_secondary' onClick={() => updateProfile(id, { monitoringWindows: [...(profile.monitoringWindows || []), { reviewId: newId(), name: 'Monitoring review', opensAt: '', dueAt: '' }] })}><FaPlus /> Add monitoring review</button>
            <div className='pmAdmin_row'>
              <label>Final review opens<input type='date' value={profile.finalReview?.opensAt || ''} onChange={(e) => updateProfile(id, { finalReview: { ...(profile.finalReview || {}), opensAt: e.target.value } })} /></label>
              <label>Final review due<input type='date' value={profile.finalReview?.dueAt || ''} onChange={(e) => updateProfile(id, { finalReview: { ...(profile.finalReview || {}), dueAt: e.target.value } })} /></label>
            </div>
            <label>Allowed templates<select multiple value={profile.templates || []} onChange={(e) => updateProfile(id, { templates: Array.from(e.target.selectedOptions, (option) => option.value) })}>
              {Object.entries(templates).map(([templateId, template]) => <option value={templateId} key={templateId}>{template.name || templateId}</option>)}
            </select></label>
          </article>)}
          <button className='pmAdmin_addCard' type='button' onClick={() => setProfiles((current) => ({ ...current, [newId()]: { profileName: 'New academic year', available: false, templates: [], objectiveSetting: {}, monitoringWindows: [], finalReview: {} } }))}><FaPlus /> Add academic year</button>
        </section>}

        {tab === 'templates' && <section className='pmAdmin_templateSection'>
          <div className='pmAdmin_templateToolbar'>
            <p><strong>{Object.keys(templates).length}</strong> templates</p>
            <button className='pmAdmin_secondary' type='button' onClick={() => openTemplateModal(newId(), emptyTemplate())}><FaPlus /> Add template</button>
          </div>
          <div className='pmAdmin_templateTableWrap'>
            <table className='pmAdmin_templateTable'>
              <thead><tr><th>Name</th><th>Status</th><th>Objectives</th><th>Questions</th><th><span className='pmAdmin_srOnly'>Actions</span></th></tr></thead>
              <tbody>
                {Object.keys(templates).length === 0 && <tr className='pmAdmin_templateEmptyRow'>
                  <td colSpan='5'>No templates have been created yet.</td>
                </tr>}
                {Object.entries(templates)
                  .sort(([, first], [, second]) => String(first.name || '').localeCompare(String(second.name || '')))
                  .map(([id, template]) => <tr key={id}>
                    <td data-label='Name'><strong>{template.name || 'Unnamed template'}</strong></td>
                    <td data-label='Status'><span className={`pmAdmin_statusBadge pmAdmin_statusBadge--${String(template.status || 'DRAFT').toLowerCase()}`}>{template.status || 'DRAFT'}</span></td>
                    <td data-label='Objectives'>{template.maxObjectives || 3}</td>
                    <td data-label='Questions'>{Object.values(template.sections || {}).reduce((total, fields) => total + (fields || []).length, 0)}</td>
                    <td className='pmAdmin_templateActions'>
                      <button type='button' aria-label={`Manage people for ${template.name || 'template'}`} onClick={() => openTemplatePeopleModal(id, template)}><FaUsers /></button>
                      <button type='button' aria-label={`Duplicate ${template.name || 'template'}`} onClick={() => openDuplicateModal(id, template)}><FaCopy /></button>
                      <button type='button' aria-label={`Edit ${template.name || 'template'}`} onClick={() => openTemplateModal(id, template)}><FaPen /></button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>}

        {templateModal && <div className='pmAdmin_modalBackdrop' role='presentation' onMouseDown={(event) => { if (event.target === event.currentTarget) setTemplateModal(null); }}>
          <section className='pmAdmin_templateModal' role='dialog' aria-modal='true' aria-labelledby='pmAdmin_templateModalTitle'>
            <button type='button' className='pmAdmin_modalClose' aria-label='Close template editor' onClick={() => setTemplateModal(null)}>×</button>
            <div className='pmAdmin_modalHeader'>
              <p>Template editor</p>
              <h2 id='pmAdmin_templateModalTitle'>{templateModal.template.name || 'Unnamed template'}</h2>
            </div>
            <div className='pmAdmin_modalBody'>
              <article className='pmAdmin_card pmAdmin_templateEditor'>
            <div className='pmAdmin_row'>
              <label>Name<input value={templateModal.template.name || ''} onChange={(e) => updateTemplateDraft({ name: e.target.value })} /></label>
              <label>Minimum objectives<input type='number' min='1' max='20' value={templateModal.template.minObjectives || 1} onChange={(e) => updateTemplateDraft({ minObjectives: Number(e.target.value) })} /></label>
              <label>Objectives<input type='number' min='1' max='20' value={templateModal.template.maxObjectives || 3} onChange={(e) => updateTemplateDraft({ maxObjectives: Number(e.target.value) })} /></label>
              <label>Status<select value={templateModal.template.status || 'DRAFT'} onChange={(e) => updateTemplateDraft({ status: e.target.value })}><option>DRAFT</option><option>PUBLISHED</option><option>RETIRED</option></select></label>
            </div>
            {[
              ['objectiveSetting', 'Objective setting'],
              ['monitoring', 'Monitoring reviews'],
              ['finalReview', 'Final review'],
              ['cpd', 'CPD log'],
            ].map(([sectionName, sectionLabel]) => <section className='pmAdmin_lifecycleSection' key={sectionName}>
              <h3>{sectionLabel}</h3>
            {(templateModal.template.sections?.[sectionName] || []).map((field, index) => <div className='pmAdmin_field' key={field.id || index}>
              <label className='pmAdmin_fieldControl'>
                <span>Question</span>
                <input aria-label='Question' value={field.label || ''} onChange={(e) => updateField(sectionName, index, { label: e.target.value })} />
              </label>
              <label className='pmAdmin_fieldControl'>
                <span>Type</span>
                <select aria-label='Question type' value={field.type || 'TEXT'} onChange={(e) => updateField(sectionName, index, { type: e.target.value })}><option>TEXT</option><option>TEXTAREA</option><option>DROPDOWN</option><option>DATE</option><option>NUMBER</option><option>CHECKBOX</option></select>
              </label>
              <label className='pmAdmin_fieldControl'>
                <span>Completed by</span>
                <select value={field.owner || 'STAFF'} onChange={(e) => updateField(sectionName, index, { owner: e.target.value })}><option value='STAFF'>Staff</option><option value='MANAGER'>Manager</option><option value='BOTH'>Staff or manager</option></select>
              </label>
              <label className='pmAdmin_check'><input type='checkbox' checked={field.required === true} onChange={(e) => updateField(sectionName, index, { required: e.target.checked })} /> Required</label>
              {field.type === 'DROPDOWN' && <div className='pmAdmin_options'>
                <span className='pmAdmin_optionsLabel'>Dropdown options</span>
                {(field.values || []).map((option, optionIndex) => (
                  <div className='pmAdmin_optionRow' key={option.id || optionIndex}>
                    <input
                      aria-label={`Dropdown option ${optionIndex + 1}`}
                      placeholder={`Option ${optionIndex + 1}`}
                      value={option.text || option.label || ''}
                      onChange={(event) => updateDropdownOption(sectionName, index, optionIndex, event.target.value)}
                    />
                    <button
                      type='button'
                      aria-label={`Remove dropdown option ${optionIndex + 1}`}
                      onClick={() => removeDropdownOption(sectionName, index, optionIndex)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
                <button
                  type='button'
                  className='pmAdmin_secondary pmAdmin_addOption'
                  onClick={() => addDropdownOption(sectionName, index)}
                >
                  <FaPlus /> Add option
                </button>
              </div>}
              <button type='button' className='pmAdmin_removeField' aria-label='Remove field' onClick={() => removeField(sectionName, index)}><FaTrash /></button>
            </div>)}
            <button type='button' className='pmAdmin_secondary' onClick={() => addField(sectionName)}><FaPlus /> Add {sectionLabel.toLowerCase()} field</button>
            </section>)}
              </article>
            </div>
            <div className='pmAdmin_modalActions'>
              <button type='button' className='pmAdmin_modalCancel' onClick={() => setTemplateModal(null)}>Cancel</button>
              <button type='button' className='pmAdmin_modalApply' onClick={applyTemplateDraft}>Apply changes</button>
            </div>
          </section>
        </div>}

        {duplicateModal && <div className='pmAdmin_modalBackdrop' role='presentation' onMouseDown={(event) => { if (event.target === event.currentTarget) setDuplicateModal(null); }}>
          <section className='pmAdmin_duplicateModal' role='dialog' aria-modal='true' aria-labelledby='pmAdmin_duplicateModalTitle'>
            <button type='button' className='pmAdmin_modalClose' aria-label='Close duplicate template dialog' onClick={() => setDuplicateModal(null)}>×</button>
            <div className='pmAdmin_modalHeader'>
              <p>Duplicate template</p>
              <h2 id='pmAdmin_duplicateModalTitle'>{duplicateModal.sourceTemplate.name || 'Unnamed template'}</h2>
            </div>
            <form className='pmAdmin_duplicateForm' onSubmit={duplicateTemplate}>
              <label htmlFor='pmAdmin_duplicateName'>New template name</label>
              <input
                id='pmAdmin_duplicateName'
                autoFocus
                maxLength='120'
                value={duplicateModal.name}
                onChange={(event) => setDuplicateModal((current) => ({ ...current, name: event.target.value, error: '' }))}
              />
              {duplicateModal.error && <p className='pmAdmin_duplicateError' role='alert'>{duplicateModal.error}</p>}
              <div className='pmAdmin_modalActions'>
                <button type='button' className='pmAdmin_modalCancel' onClick={() => setDuplicateModal(null)}>Cancel</button>
                <button type='submit' className='pmAdmin_modalApply'>Duplicate template</button>
              </div>
            </form>
          </section>
        </div>}

        {templatePeopleModal && <div className='pmAdmin_modalBackdrop' role='presentation' onMouseDown={(event) => { if (event.target === event.currentTarget) setTemplatePeopleModal(null); }}>
          <section className='pmAdmin_peopleModal' role='dialog' aria-modal='true' aria-labelledby='pmAdmin_peopleModalTitle'>
            <button type='button' className='pmAdmin_modalClose' aria-label='Close staff access' onClick={() => setTemplatePeopleModal(null)}>×</button>
            <div className='pmAdmin_modalHeader'>
              <p>Template access</p>
              <h2 id='pmAdmin_peopleModalTitle'>{templatePeopleModal.templateName}</h2>
            </div>
            <div className='pmAdmin_peopleList'>
              {sortedStaff.map((person) => {
                const enabled = Array.isArray(person.templateIds) && person.templateIds.includes(templatePeopleModal.templateId);
                const updating = updatingTemplateStaff[person.cogId] === true;
                return <div className='pmAdmin_peopleRow' key={person.cogId}>
                  <div className='pmAdmin_peopleIdentity'><strong>{person.displayName}</strong><small>{person.email}</small></div>
                  <div className='pmAdmin_peopleChoices' aria-label={`Template access for ${person.displayName}`}>
                    <label><input type='radio' name={`template-access-${person.cogId}`} checked={enabled} disabled={updating} onChange={() => changeTemplateAccess(person, true)} /> Enabled</label>
                    <label><input type='radio' name={`template-access-${person.cogId}`} checked={!enabled} disabled={updating} onChange={() => changeTemplateAccess(person, false)} /> Disabled</label>
                  </div>
                  {updating && <span className='pmAdmin_peopleSaving'>Saving…</span>}
                </div>;
              })}
              {sortedStaff.length === 0 && <p className='pmAdmin_peopleEmpty'>No active staff were found.</p>}
            </div>
          </section>
        </div>}

        {tab === 'assignments' && <section className='pmAdmin_stack'>
          {sortedStaff.map((person) => <article className='pmAdmin_assignment' key={person.cogId}>
            <div><strong>{person.displayName}</strong><small>{person.email}</small></div>
            <select value={person.managerCogId || ''} onChange={(e) => changeManager(person, e.target.value)}>
              <option value=''>No manager assigned</option>
              {sortedStaff.filter((manager) => manager.cogId !== person.cogId).map((manager) => <option value={manager.cogId} key={manager.cogId}>{manager.displayName}</option>)}
            </select>
          </article>)}
        </section>}

        {tab === 'progress' && <section className='pmAdmin_stack'>
          <label>Academic year<select value={progress.profileId || ''} onChange={(event) => loadProgress(event.target.value)}><option value=''>Select academic year</option>{Object.entries(profiles).map(([profileId, profile]) => <option key={profileId} value={profileId}>{profile.profileName}</option>)}</select></label>
          {progress.profileId && <>
            <div className='pmAdmin_progressSummary'>{Object.entries(progress.counts || {}).map(([status, count]) => <div key={status}><strong>{count}</strong><span>{status.replaceAll('_', ' ')}</span></div>)}</div>
            <div className='pmAdmin_templateTableWrap'><table className='pmAdmin_templateTable'><thead><tr><th>Staff member</th><th>Status</th><th>Due</th><th>Template</th><th><span className='pmAdmin_srOnly'>Actions</span></th></tr></thead><tbody>{(progress.records || []).map((record) => <tr key={record.recordId}><td data-label='Staff member'>{record.staffName}</td><td data-label='Status'>{String(record.status || '').replaceAll('_', ' ')}</td><td data-label='Due'>{record.dueAt || 'Not set'}</td><td data-label='Template'>{record.templateName}</td><td className='pmAdmin_progressActions'><button type='button' aria-label={`Delete ${record.staffName}'s ${record.templateName} record`} title='Delete record' onClick={() => setDeleteRecordModal(record)}><FaTrash aria-hidden='true' /></button></td></tr>)}</tbody></table></div>
          </>}
        </section>}

        {deleteRecordModal && <div className='pmAdmin_modalBackdrop' role='presentation' onMouseDown={(event) => { if (event.target === event.currentTarget && !deletingRecordId) setDeleteRecordModal(null); }}>
          <section className='pmAdmin_deleteModal' role='dialog' aria-modal='true' aria-labelledby='pmAdmin_deleteRecordTitle'>
            <button type='button' className='pmAdmin_modalClose' disabled={Boolean(deletingRecordId)} aria-label='Close delete record dialog' onClick={() => setDeleteRecordModal(null)}>×</button>
            <div className='pmAdmin_modalHeader'>
              <p>Permanent deletion</p>
              <h2 id='pmAdmin_deleteRecordTitle'>Delete Performance Management record?</h2>
            </div>
            <div className='pmAdmin_deleteBody'>
              <p>This will permanently delete the record, including all objectives, monitoring reviews, final-review answers, CPD entries and history.</p>
              <dl>
                <div><dt>Staff member</dt><dd>{deleteRecordModal.staffName || 'Unknown'}</dd></div>
                <div><dt>Academic year</dt><dd>{deleteRecordModal.profileName || 'Unknown'}</dd></div>
                <div><dt>Template</dt><dd>{deleteRecordModal.templateName || 'Unknown'}</dd></div>
              </dl>
              <p className='pmAdmin_deleteWarning'>This action cannot be undone.</p>
            </div>
            <div className='pmAdmin_modalActions'>
              <button type='button' className='pmAdmin_modalCancel' disabled={Boolean(deletingRecordId)} onClick={() => setDeleteRecordModal(null)}>Cancel</button>
              <button type='button' className='pmAdmin_deleteConfirm' disabled={Boolean(deletingRecordId)} onClick={confirmDeleteRecord}>{deletingRecordId ? 'Deleting…' : 'Delete record'}</button>
            </div>
          </section>
        </div>}

        {(tab === 'profiles' || tab === 'templates') && <div className={`pmAdmin_saveState pmAdmin_saveState--${saveStatus}`} role='status' aria-live='polite'>
          {saveStatus === 'saving' || saveStatus === 'pending' ? <span>Saving changes…</span> : saveStatus === 'error' ? <><span>Changes could not be saved.</span><button type='button' onClick={retryAutoSave}>Retry</button></> : <span>All changes saved</span>}
        </div>}
      </div>
    </div>
  );
}
